import { notifyError, notifySuccess } from "@/lib/notification";
import * as applicationService from "@/services/application.service";
import {
  ApplicationCreatePayload,
  ApplicationListParams,
  ApplicationType,
} from "@/services/application.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const applicationKey = {
  all: ["applications"] as const,
  lists: (params?: ApplicationListParams) =>
    [...applicationKey.all, "list", params] as const,
  detail: (id: string) => [...applicationKey.all, "detail", id] as const,
};

// ─── Queries ──────────────────────────────────────────────────────────────────

export const useApplicationsQuery = (params?: ApplicationListParams) =>
  useQuery({
    queryKey: applicationKey.lists(params),
    queryFn: () => applicationService.fetchApplications(params),
    placeholderData: (prev) => prev,
  });

export const useApplicationByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: applicationKey.detail(id),
    queryFn: () => applicationService.fetchApplicationById(id),
    enabled: enabled && !!id,
  });

// ─── Mutations ────────────────────────────────────────────────────────────────

export const useApplyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<ApplicationType, Error, ApplicationCreatePayload>({
    mutationFn: applicationService.applyForInternship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKey.all });
      notifySuccess("Application submitted successfully! 🎉");
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.detail?.[0]?.msg ||
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "Failed to submit application";
      notifyError(msg);
    },
  });
};

export const useWithdrawApplicationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, string>({
    mutationFn: applicationService.withdrawApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKey.all });
      notifySuccess("Application withdrawn successfully.");
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "Failed to withdraw application";
      notifyError(msg);
    },
  });
};

// useSyncApplicationStatusMutation was removed: it PATCHed the unauthenticated
// internal/v1 status endpoint as a workaround for a recruiter push that landed
// on the wrong URL. The backend fixed that push, and every call re-sent the
// status-change email to the student.
